import { useEffect, useLayoutEffect, useRef, useState } from 'react'

export const SOFT_DECODE_GLYPHS = ['⋆', '·', '⟡', '⌁'] as const
export const SOFT_DECODE_REFILL_MS = 96

export function appendedUnitCount(previous: string, next: string) {
  if (!next.startsWith(previous)) return 0
  return Array.from(next.slice(previous.length)).length
}

export function visibleSoftDecodeSlotCount(previousText: string, text: string, storedSlots: number, active: boolean) {
  if (!active) return 0
  return Math.max(0, storedSlots - appendedUnitCount(previousText, text))
}

export function nativeSoftDecodeActiveBlock(streaming: boolean, thinking: string, content: string): 'thinking' | 'answer' | null {
  if (!streaming) return null
  if (content) return 'answer'
  if (thinking) return 'thinking'
  return 'answer'
}

type TimeoutScheduler = (callback: () => void, delay: number) => number

export function scheduleSoftDecodeRefill(callback: () => void, schedule: TimeoutScheduler = (cb, delay) => window.setTimeout(cb, delay)) {
  return schedule(callback, SOFT_DECODE_REFILL_MS)
}

export function SoftDecodedTextView({ text, active, slots }: { text: string; active: boolean; slots: number }) {
  const firstSlot = SOFT_DECODE_GLYPHS.length - slots
  return <span className="native-decoded-text">
    <span>{text}</span>
    {active && slots > 0 && <span className="soft-decode-slots" aria-hidden="true">
      {SOFT_DECODE_GLYPHS.slice(firstSlot).map((glyph, index) => (
        <span className="soft-decode-slot" key={`${firstSlot + index}-${glyph}`}>{glyph}</span>
      ))}
    </span>}
  </span>
}

export function SoftDecodedText({ text, active }: { text: string; active: boolean }) {
  const [slots, setSlots] = useState(active ? SOFT_DECODE_GLYPHS.length : 0)
  const previousText = useRef(text)
  const refillTimer = useRef<number | null>(null)

  // Derive the visible count during render so a new real character already occupies
  // one of the unresolved slots in the very first DOM produced for this update.
  const visibleSlots = visibleSoftDecodeSlotCount(previousText.current, text, slots, active)

  useLayoutEffect(() => {
    if (refillTimer.current !== null) {
      window.clearTimeout(refillTimer.current)
      refillTimer.current = null
    }

    previousText.current = text
    if (!active) {
      if (slots !== 0) setSlots(0)
      return
    }
    if (visibleSlots !== slots) setSlots(visibleSlots)
  }, [active, text])

  useEffect(() => {
    if (!active) return
    const timer = scheduleSoftDecodeRefill(() => {
      setSlots(SOFT_DECODE_GLYPHS.length)
      if (refillTimer.current === timer) refillTimer.current = null
    })
    refillTimer.current = timer
    return () => window.clearTimeout(timer)
  }, [active, text])

  return <SoftDecodedTextView text={text} active={active} slots={visibleSlots} />
}
